/**
 * Shared hook that resolves the user's Ghostwriter consent status.
 *
 * Resolution cascade (runs once per user session; re-runs after store
 * is cleared on user switch):
 *   1. Store already populated → no-op
 *   2. TareBridge local storage → use cached value
 *   3. Ghostwriter backend → fetch, persist locally, then store
 *   4. On error → default to hasConsented = false
 *
 * Used by both useGhostwriterForViewJob and useGhostwriterForOnboarding
 * so consent is resolved regardless of which screen mounts first.
 */

import type { ScheduleApplyConsentStatusResponse } from '../models/ScheduleApplyConsentStatus';

import { loadingAsyncState, successAsyncState, type AsyncState } from './AsyncState';
import { useGhostwriterApi, getLocalConsent, setLocalConsent } from './ghostwriterApi';
import { useGhostwriterConsentStatus } from './GhostwriterContext';
import { setConsentStatusState } from './GhostwriterStore';

import { useEffect, useRef } from 'react';

export interface ResolvedConsentStatus {
    hasConsented: boolean;
    consentLoading: boolean;
    consentError: Error | null;
    consentStatus: AsyncState<ScheduleApplyConsentStatusResponse>;
}

export function useResolveConsentStatus(enabled = true): ResolvedConsentStatus {
    const { fetchConsentStatus } = useGhostwriterApi();
    const consentStatus = useGhostwriterConsentStatus();
    const didFetchRef = useRef(false);

    useEffect(() => {
        if (!enabled || consentStatus.loading) return;
        if (consentStatus.data?.hasConsented) return;
        if (didFetchRef.current) return;

        didFetchRef.current = true;
        setConsentStatusState(loadingAsyncState<ScheduleApplyConsentStatusResponse>());

        void (async () => {
            try {
                const localValue = await getLocalConsent();

                // Accept a local true immediately; if false/null, check the
                // backend — the user may have consented on a different platform.
                if (localValue === true) {
                    setConsentStatusState(successAsyncState({ hasConsented: true }));
                    return;
                }

                const backendConsent = await fetchConsentStatus();
                await setLocalConsent(backendConsent.hasConsented);
                setConsentStatusState(successAsyncState({ hasConsented: backendConsent.hasConsented }));
            } catch {
                setConsentStatusState(successAsyncState({ hasConsented: false }));
            }
        })();
    }, [enabled, consentStatus.data, consentStatus.loading, fetchConsentStatus]);

    return {
        hasConsented: consentStatus.data?.hasConsented ?? false,
        consentLoading: consentStatus.loading,
        consentError: consentStatus.error,
        consentStatus,
    };
}
